import { useState } from 'react'
import { Plus } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ContactsTable } from './ContactsTable'
import { ContactSheet } from './ContactSheet'
import { DeleteContactDialog } from './DeleteContactDialog'
import { useContacts } from '@/hooks/useContacts'
import { useClient } from '@/hooks/useClients'
import { getClientDisplayName } from '@/types/clients'

interface ClientContactsSheetProps {
  /** Whether the dialog is open */
  open: boolean
  /** Called when open state changes */
  onOpenChange: (open: boolean) => void
  /** Client ID to show contacts for */
  clientId: string
}

/**
 * Dialog showing all contacts for a specific client
 * With ability to add, edit, and delete contacts
 */
export function ClientContactsSheet({
  open,
  onOpenChange,
  clientId,
}: ClientContactsSheetProps) {
  // Fetch client details
  const { data: client } = useClient(clientId)

  // Fetch contacts for this client
  const { data: contacts, isLoading } = useContacts(clientId)

  // Contact edit sheet state
  const [contactSheetState, setContactSheetState] = useState<{
    open: boolean
    contactId?: string
  }>({ open: false })

  // Delete dialog state
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    name: string
  } | null>(null)

  const handleAddContact = () => {
    setContactSheetState({ open: true, contactId: undefined })
  }

  const handleEditContact = (id: string) => {
    setContactSheetState({ open: true, contactId: id })
  }

  const handleDeleteContact = (id: string, name: string) => {
    setDeleteTarget({ id, name })
  }

  const handleContactSheetClose = (open: boolean) => {
    setContactSheetState((prev) => ({ ...prev, open }))
  }

  const handleDeleteDialogClose = (open: boolean) => {
    if (!open) {
      setDeleteTarget(null)
    }
  }

  const clientName = client ? getClientDisplayName(client) : 'Client'

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Contacts for {clientName}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-auto space-y-4">
            {/* Add Contact Button */}
            <div className="flex justify-end">
              <Button onClick={handleAddContact} size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Add Contact
              </Button>
            </div>

            {/* Contacts Table */}
            <ContactsTable
              data={contacts || []}
              isLoading={isLoading}
              onEdit={handleEditContact}
              onDelete={handleDeleteContact}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Edit Sheet */}
      {client && (
        <ContactSheet
          open={contactSheetState.open}
          onOpenChange={handleContactSheetClose}
          clientId={clientId}
          organizationId={client.organization_id}
          contactId={contactSheetState.contactId}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <DeleteContactDialog
          open={!!deleteTarget}
          onOpenChange={handleDeleteDialogClose}
          contactId={deleteTarget.id}
          clientId={clientId}
          contactName={deleteTarget.name}
        />
      )}
    </>
  )
}
